import * as v from 'valibot';

const Id = v.pipe(v.string(), v.uuid());
const Integer = v.pipe(v.number(), v.integer());
const String = v.pipe(v.string(), v.minLength(1));
const Extra = v.nullish(v.any());
const Edge = <T extends v.GenericSchema> (type: T) => v.object({ nodes: v.array(type) });

export const CounterNodeSchema = v.object({
	id: Id,
	version: Id,
	team_id: v.nullish(Id),
	name: v.pipe(v.string(), v.minLength(1)),
	value: Integer,
	extra: Extra,
});

export type CounterNode = v.InferInput<typeof CounterNodeSchema>;

export const DurationSchema = v.object({
	years: Integer,
	months: Integer,
	days: Integer,
	hours: Integer,
	minutes: Integer,
	seconds: v.number(),
});

export type Duration = v.InferInput<typeof DurationSchema>;

export const TimerNodeSchema = v.object({
	id: Id,
	version: Id,
	team_id: v.nullish(Id),
	name: String,
	value: DurationSchema,
	start_event_id: v.nullish(Id),
	direction: v.nullish(v.pipe(v.number(), v.values([-1, 1]))),
	extra: Extra,
});

export type TimerNode = v.InferInput<typeof TimerNodeSchema>;

export const TeamNodeSchema = v.object({
	id: Id,
	name: String,
	extra: Extra,
});

export type TeamNode = v.InferInput<typeof TeamNodeSchema>;

export const GameNodeSchema = v.object({
	id: Id,
	version: Id,
	location_id: Id,
	home_team: TeamNodeSchema,
	away_team: TeamNodeSchema,
	start_event_id: v.nullish(Id),
	end_event_id: v.nullish(Id),
	extra: Extra,
	counters: Edge(CounterNodeSchema),
	timers: Edge(TimerNodeSchema),
});

export type GameNode = v.InferInput<typeof GameNodeSchema>;

export const LocationNodeSchema = v.object({
	id: Id,
	customer_id: Id,
	name: String,
	time_zone: String,
	extra: Extra,
	games: Edge(GameNodeSchema),
});

export type LocationNode = v.InferInput<typeof LocationNodeSchema>;

function gqlFragment (schema: v.GenericSchema, prefix = '', indent = '\t') {
	if (schema.type === 'object') {
		let str = ' {\n';

		const object_schema = schema as v.ObjectSchema<any, any>;
		for (const key in object_schema.entries) {
			str += prefix;
			str += indent;
			str += key.includes('_') ? `${key}: ${camelCase(key)}` : key;
			str += gqlFragment(object_schema.entries[key], prefix + indent, indent);
			str += '\n';
		}

		return str + `${prefix}}`;
	} else if (schema.type === 'array') {
		return gqlFragment((schema as v.ArraySchema<any, any>).item, prefix, indent);
	} else if (schema.type === 'optional') {
		return gqlFragment((schema as v.OptionalSchema<any, any>).wrapped, prefix, indent);
	} else {
		return '';
	}
}

function camelCase (snake_case: string) {
	return snake_case.replace(/_(.)/g, (_, c) => c.toUpperCase());
}

export const game_query = `query($game_id: UUID!) {
	game(id: $game_id) ${gqlFragment(GameNodeSchema, '\t')}
}`;

export const location_query = `query($location_id: UUID!) {
	location(id: $location_id) {
		id
		customer_id: customerId
		name
		time_zone: timeZone
		extra
		games(
			filter: {
				startEventId: { isNull: false }
				endEventId: { isNull: true }
			}
		) {
			nodes ${gqlFragment(GameNodeSchema, '\t\t\t')}
		}
	}
}`;
